/**
 * Journal page - daily prompt and journal entries, stored on the server (/api/journal).
 * Every plan and the trial include it.
 */
import { useState } from 'react';
import { BookOpen, Lock, Plus, Calendar, Download, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, Button } from '@/components/ui';
import { JournalEntryModal } from '@/components/features';
import { useLanguage, useAuthStore } from '@/store';
import { useNavigate } from 'react-router-dom';
import { getJournalTranslation, getJournalExportTranslation, formatDate } from '@/utils';
import { hasAccess } from '@/utils/access';
import { useJournalEntries, useCreateJournalEntry, useDeleteJournalEntry, journalTitle } from '@/hooks/useJournal';

export function JournalPage() {
  const language = useLanguage();
  const { user, isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  // Every plan and the trial include the Journal (R2, R5).
  const isPremium = hasAccess(user);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const entriesQuery = useJournalEntries(user?.id, isAuthenticated && isPremium);
  const createEntry = useCreateJournalEntry();
  const deleteEntry = useDeleteJournalEntry();
  const entries = entriesQuery.data?.entries ?? [];

  const t = (key: Parameters<typeof getJournalTranslation>[0]) => getJournalTranslation(key, language);
  const te = (key: Parameters<typeof getJournalExportTranslation>[0]) => getJournalExportTranslation(key, language);

  /** Rejects on failure so the dialog stays open with the text still in it. */
  const handleNewEntry = async (data: { content: string; moodBefore: string; moodAfter: string }) => {
    setSaveError(null);
    try {
      await createEntry.mutateAsync({
        title: journalTitle(data.content),
        content: data.content,
        mood_before: data.moodBefore,
        mood_after: data.moodAfter || undefined,
      });
    } catch (error) {
      setSaveError(t('saveError'));
      throw error;
    }
  };

  const handleModalOpenChange = (open: boolean) => {
    if (!open) setSaveError(null);
    setIsModalOpen(open);
  };

  const handleDelete = (entryId: string) => {
    if (!window.confirm(t('deleteConfirm'))) return;
    setDeleteError(null);
    deleteEntry.mutate(entryId, { onError: () => setDeleteError(t('saveError')) });
  };

  const handleExport = () => {
    if (entries.length === 0) return;

    const exportContent = entries
      .map((entry) => {
        const date = formatDate(entry.created_at, language);
        return `${te('exportDate')}: ${date}\n${te('exportMoodBefore')}: ${entry.mood_before ?? te('exportNA')}\n${te('exportMoodAfter')}: ${entry.mood_after || te('exportNA')}\n\n${entry.content}\n\n---\n`;
      })
      .join('\n');

    const blob = new Blob([exportContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `journal-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isAuthenticated) {
    return (
      <div className="container py-16 max-w-lg mx-auto text-center">
        <Card>
          <CardHeader>
            <div className="flex justify-center mb-4">
              <Lock className="h-16 w-16 text-muted-foreground" />
            </div>
            <CardTitle>{t('loginRequired')}</CardTitle>
            <CardDescription>{t('loginDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/login')}>{t('login')}</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isPremium) {
    return (
      <div className="container py-16 max-w-lg mx-auto text-center">
        <Card className="gradient-border">
          <CardHeader>
            <div className="flex justify-center mb-4">
              <div className="relative">
                <BookOpen className="h-16 w-16 text-primary" />
                <Lock className="h-6 w-6 text-lukaaji-gold absolute -bottom-1 -right-1" />
              </div>
            </div>
            <CardTitle className="text-gradient">{t('premiumFeature')}</CardTitle>
            <CardDescription>{t('premiumDesc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">{t('premiumUse')}</p>
            <Button className="gradient-primary" onClick={() => navigate('/pricing')}>
              {t('upgrade')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Premium user - show journal interface
  return (
    <>
      <div className="container py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-2">{t('title')}</h1>
            <p className="text-muted-foreground">{t('subtitle')}</p>
          </div>
          <Button className="gradient-primary" onClick={() => setIsModalOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t('newEntry')}
          </Button>
        </div>

        {/* Daily Prompt */}
        <Card className="mb-6 gradient-primary text-white">
          <CardContent className="py-6">
            <h3 className="font-semibold mb-2">{t('todaysPrompt')}</h3>
            <p className="text-lg italic">{t('promptText')}</p>
          </CardContent>
        </Card>

        {/* Journal entries */}
        <div className="space-y-4 mb-6">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="h-4 w-4" />
            <span>{t('recentEntries')}</span>
          </div>

          {deleteError && (
            <p role="alert" className="text-sm text-destructive">{deleteError}</p>
          )}

          {entriesQuery.isError ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p role="alert" className="text-muted-foreground">{t('loadError')}</p>
              </CardContent>
            </Card>
          ) : entriesQuery.isPending ? null : entries.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">{t('noEntries')}</p>
              </CardContent>
            </Card>
          ) : (
            entries.map((entry) => (
              <Card key={entry.id}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="text-sm text-muted-foreground">
                      {formatDate(entry.created_at, language)}
                    </span>
                    <div className="flex flex-wrap items-center justify-end gap-2 text-xs">
                      {entry.mood_before && (
                        <span className="bg-muted px-2 py-1 rounded">
                          {t('before')}: {entry.mood_before}
                        </span>
                      )}
                      {entry.mood_after && (
                        <span className="bg-primary/10 text-primary px-2 py-1 rounded">
                          {t('after')}: {entry.mood_after}
                        </span>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-11 w-11 shrink-0"
                        aria-label={t('deleteEntry')}
                        title={t('deleteEntry')}
                        disabled={deleteEntry.isPending && deleteEntry.variables === entry.id}
                        onClick={() => handleDelete(entry.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm whitespace-pre-wrap break-words">{entry.content}</p>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Insights */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">{t('insights')}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">{t('insightsText')}</p>
          </CardContent>
        </Card>

        {/* Export Button */}
        <Button
          variant="outline"
          onClick={handleExport}
          disabled={entries.length === 0}
          className="w-full"
        >
          <Download className="h-4 w-4 mr-2" />
          {t('exportJournal')}
        </Button>
      </div>

      {/* Journal Entry Modal */}
      <JournalEntryModal
        open={isModalOpen}
        onOpenChange={handleModalOpenChange}
        onSubmit={handleNewEntry}
        error={saveError}
      />
    </>
  );
}
